import type { ProjectWrapperType } from "@/dataProviders/ProjectWrapper";

type Project = ProjectWrapperType["project"];

const modelRowNames = (project: Project): Set<string> =>
  new Set([...(project.speckleData?.branches ?? []), ...(project.speckleData?.inactiveBranches ?? [])].map((row) => row.name));

// The API's list (active models plus names only materials carry), with the models the save added
// joined and those it removed dropped; the save owns the model rows.
const mergeModelNames = (current: Project, saved: Project): string[] | undefined => {
  if (!current.modelNames) return current.modelNames;
  const savedRows = modelRowNames(saved);
  const removed = [...modelRowNames(current)].filter((name) => !savedRows.has(name));
  const added = (saved.speckleData?.branches ?? []).map((row) => row.name);
  return [...new Set([...current.modelNames.filter((name) => !removed.includes(name)), ...added])].sort();
};

/**
 * The dashboard's project store re-read from the settings page's wrapper after a save (RTLCA-5645).
 *
 * The two stores hold the same DTO shape from different reads: the dashboard's from `GET api/BuildingProject/{id}`,
 * the settings page's from Hasura. Everything the settings page can change is taken from the saved copy, but for the
 * REST-only `calculationType` and `apsData`, and `modelNames`, which also names models only materials carry and is
 * updated rather than replaced. A store holding another project, or none, is left alone: the layout loads it on arrival.
 */
export const mergeSavedProject = (current: Project | null, saved: Project): Project | null => {
  if (!current || current.id !== saved.id) return current;
  const { calculationType: _calculationType, modelNames: _modelNames, apsData: _apsData, ...settingsOwned } = saved;
  return { ...current, ...settingsOwned, modelNames: mergeModelNames(current, saved) };
};

export default mergeSavedProject;
